/**
 * The dropdown the top bar's three controls share.
 *
 * ARCH §7.3 asks for headless primitives "for behaviour, hand-written visuals
 * matched to `design/screens/`" — the parts that carry focus traps, keyboard
 * navigation and ARIA, "which PRD §1.7's visible keyboard focus on every
 * interactive element requires and which are the expensive half to get right".
 *
 * This is that behaviour, written rather than installed: Escape closes, an
 * outside click closes, focus returns to the trigger on close, and the trigger
 * carries `aria-expanded` / `aria-haspopup`. A dependency for one popover would
 * be a dependency the funnel editor then has to live with; when Epic 6 needs a
 * real focus trap and a listbox, that is the story to bring one in.
 *
 * Visuals are read off `_system-sheet.html` §05, avatar menu open: `196px`
 * (`--mdx-menu-width`), radius `--mdx-radius-control`, `--mdx-shadow-card`,
 * `--mdx-space-6` padding, `--mdx-space-2` between items.
 *
 * Story 3.8.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'

/** The padded pill the workspace and organisation switchers use. */
const TRIGGER =
  'flex min-w-0 cursor-pointer items-center gap-mdx-8 rounded-mdx-control border-0 bg-transparent px-mdx-12 py-mdx-9 font-mdx-semibold text-mdx-15 text-mdx-ink transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground'

export function Menu({
  trigger,
  label,
  children,
  align = 'right',
  triggerClassName = TRIGGER,
}: {
  /** Rendered inside the trigger button. */
  trigger: ReactNode
  /** The accessible name of the trigger. */
  label: string
  /** The menu's contents — `MenuItem`, `MenuHeader`. */
  children: ReactNode
  align?: 'left' | 'right'
  /**
   * The switchers are padded pills; the avatar is a bare 38px circle. Both are
   * in `_system-sheet.html` §05, so the trigger's box belongs to the caller
   * rather than to a variant enum with two members.
   */
  triggerClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      // Focus goes back where it came from. A menu that closes into nowhere
      // strands a keyboard user at the top of the document.
      triggerRef.current?.focus()
    }
    const onPointer = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [open])

  return (
    <div className="relative" ref={root}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open ? (
        <div
          role="menu"
          // `top-full mt-mdx-8` rather than a 46px literal: the sheet's offset
          // is the trigger's own height plus the gap, so composing it keeps the
          // two in step if either changes.
          className={[
            'absolute top-full z-10 mt-mdx-8 flex w-mdx-menu flex-col gap-mdx-2',
            'rounded-mdx-control border border-mdx-line bg-mdx-white p-mdx-6 shadow-mdx-card',
            align === 'right' ? 'right-0' : 'left-0',
          ].join(' ')}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      ) : null}
    </div>
  )
}

/** The identity block above the items — name over email, on a soft divider. */
export function MenuHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-mdx-4 border-b border-mdx-line-soft px-mdx-10 pb-mdx-10 pt-mdx-8">
      <div className="truncate text-mdx-14 font-mdx-semibold">{title}</div>
      <div className="truncate text-mdx-13 text-mdx-slate">{subtitle}</div>
    </div>
  )
}

const ITEM =
  'block w-full cursor-pointer rounded-mdx-control-sm border-0 bg-transparent px-mdx-10 py-mdx-9 text-left text-mdx-15 text-mdx-ink no-underline transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground'

export function MenuLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a role="menuitem" href={href} className={ITEM}>
      {children}
    </a>
  )
}

export function MenuButton({
  onClick,
  destructive = false,
  children,
}: {
  onClick: () => void
  /**
   * `--mdx-error`, which `DESIGN-SYSTEM.md` §1 reserves for "errors and
   * destructive actions only". `settings-workspaces.html` draws Archive this
   * way in the card menu, beside two ordinary items — Story 4.2.
   */
  destructive?: boolean
  children: ReactNode
}) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      className={destructive ? `${ITEM} text-mdx-error` : ITEM}
    >
      {children}
    </button>
  )
}

/** A chosen row in a switcher — the tick is the only difference from a link. */
export function MenuChoice({
  onClick,
  selected,
  children,
}: {
  onClick: () => void
  selected: boolean
  children: ReactNode
}) {
  return (
    <button
      role="menuitemradio"
      aria-checked={selected}
      type="button"
      onClick={onClick}
      className={`${ITEM} ${selected ? 'font-mdx-semibold text-mdx-brand-deep' : ''}`}
    >
      {children}
    </button>
  )
}
